import React, { useState, useMemo } from 'react';
import { FiSearch, FiFilter, FiLayers, FiList, FiGrid, FiX } from 'react-icons/fi';

/*
  Reusable Odoo-style list view.
  props:
    title, eyebrow, count            — header text
    columns: [{ key, label, render?, width? }]
    rows: array of records
    getRowKey(row)                   — unique key
    onRowClick(row)                  — open record
    searchKeys: [keys]               — which fields the search box scans
    filters: [{ key, label, options:[{value,label}] }]  — dropdown filters (client-side)
    groupBy: [{ key, label }]        — fields the user can group by
    rightAction                      — e.g. a "New" button node
    views: ['list','kanban']         — which view toggles to show (kanban optional)
    onViewChange(v)                  — when the user switches view
    view                             — current view ('list' default)
*/
export default function OdooListView({
  title, eyebrow, count, columns, rows, getRowKey, onRowClick,
  searchKeys = [], filters = [], groupBy = [], rightAction,
  views = ['list'], view = 'list', onViewChange, renderBody = null,
}) {
  const [search, setSearch] = useState('');
  const [active, setActive] = useState({});     // {filterKey: value}
  const [group, setGroup] = useState('');        // groupBy key
  const [showFilter, setShowFilter] = useState(false);
  const [showGroup, setShowGroup] = useState(false);

  const filtered = useMemo(() => {
    let out = rows || [];
    if (search && searchKeys.length) {
      const s = search.toLowerCase();
      out = out.filter((r) => searchKeys.some((k) => String(r[k] ?? '').toLowerCase().includes(s)));
    }
    Object.entries(active).forEach(([k, v]) => {
      if (v !== '' && v != null) out = out.filter((r) => String(r[k] ?? '') === String(v));
    });
    return out;
  }, [rows, search, searchKeys, active]);

  const grouped = useMemo(() => {
    if (!group) return null;
    const map = {};
    filtered.forEach((r) => {
      const key = r[group] ?? '—';
      (map[key] = map[key] || []).push(r);
    });
    return Object.entries(map).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  }, [filtered, group]);

  const activeChips = Object.entries(active).filter(([, v]) => v !== '' && v != null);

  return (
    <div className="olv">
      {/* ===== Control panel ===== */}
      <div className="olv-head">
        <div>
          {eyebrow && <div className="olv-eyebrow">{eyebrow}</div>}
          <h1 className="olv-title">{title}</h1>
          <div className="olv-count">{count}</div>
        </div>
        <div className="olv-actions">{rightAction}</div>
      </div>

      <div className="olv-cp">
        <div className="olv-search">
          <FiSearch />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" />
          {search && <button className="olv-x" onClick={() => setSearch('')}><FiX /></button>}
        </div>

        {filters.length > 0 && (
          <div className="olv-drop-wrap">
            <button className={'olv-cp-btn' + (activeChips.length ? ' on' : '')} onClick={() => { setShowFilter((v) => !v); setShowGroup(false); }}>
              <FiFilter /> Filters {activeChips.length ? `(${activeChips.length})` : ''}
            </button>
            {showFilter && (
              <div className="olv-drop">
                {filters.map((f) => (
                  <div key={f.key} className="olv-drop-sec">
                    <div className="olv-drop-label">{f.label}</div>
                    {f.options.map((o) => (
                      <button key={o.value} className={'olv-drop-opt' + (String(active[f.key]) === String(o.value) ? ' sel' : '')}
                        onClick={() => setActive((a) => ({ ...a, [f.key]: String(a[f.key]) === String(o.value) ? '' : o.value }))}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {groupBy.length > 0 && (
          <div className="olv-drop-wrap">
            <button className={'olv-cp-btn' + (group ? ' on' : '')} onClick={() => { setShowGroup((v) => !v); setShowFilter(false); }}>
              <FiLayers /> Group By {group ? '(1)' : ''}
            </button>
            {showGroup && (
              <div className="olv-drop">
                {groupBy.map((g) => (
                  <button key={g.key} className={'olv-drop-opt' + (group === g.key ? ' sel' : '')}
                    onClick={() => { setGroup(group === g.key ? '' : g.key); setShowGroup(false); }}>
                    {g.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="olv-spacer" />

        {views.length > 1 && (
          <div className="olv-viewswitch">
            {views.includes('list') && <button className={'olv-vs' + (view === 'list' ? ' on' : '')} onClick={() => onViewChange && onViewChange('list')} title="List"><FiList /></button>}
            {views.includes('kanban') && <button className={'olv-vs' + (view === 'kanban' ? ' on' : '')} onClick={() => onViewChange && onViewChange('kanban')} title="Kanban"><FiGrid /></button>}
          </div>
        )}
      </div>

      {/* active filter chips */}
      {(activeChips.length > 0 || group) && (
        <div className="olv-chips">
          {activeChips.map(([k, v]) => {
            const f = filters.find((x) => x.key === k);
            const opt = f && f.options.find((o) => String(o.value) === String(v));
            return <span key={k} className="olv-chip">{f ? f.label : k}: {opt ? opt.label : v}<button onClick={() => setActive((a) => ({ ...a, [k]: '' }))}><FiX /></button></span>;
          })}
          {group && <span className="olv-chip grp">Grouped by {groupBy.find((g) => g.key === group)?.label}<button onClick={() => setGroup('')}><FiX /></button></span>}
        </div>
      )}

      {/* ===== Body: custom (kanban) or list ===== */}
      {renderBody ? (
        <div className="olv-body">{renderBody}</div>
      ) : (
      <div className="olv-card">
        {filtered.length === 0 ? (
          <div className="olv-empty"><h3>No records</h3><p>Try adjusting your search or filters.</p></div>
        ) : (
          <table className="olv-table">
            <thead><tr>{columns.map((c) => <th key={c.key} style={c.width ? { width: c.width } : null}>{c.label}</th>)}</tr></thead>
            <tbody>
              {grouped ? grouped.map(([gk, items]) => (
                <React.Fragment key={gk}>
                  <tr className="olv-grouprow"><td colSpan={columns.length}><span className="olv-grouplabel">{String(gk)}</span><span className="olv-groupcount">{items.length}</span></td></tr>
                  {items.map((r) => <Row key={getRowKey(r)} row={r} columns={columns} onRowClick={onRowClick} />)}
                </React.Fragment>
              )) : filtered.map((r) => <Row key={getRowKey(r)} row={r} columns={columns} onRowClick={onRowClick} />)}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}

function Row({ row, columns, onRowClick }) {
  return (
    <tr className={onRowClick ? 'olv-rowclick' : ''} onClick={() => onRowClick && onRowClick(row)}>
      {columns.map((c) => <td key={c.key}>{c.render ? c.render(row) : (row[c.key] ?? '—')}</td>)}
    </tr>
  );
}
